// LocationForm.tsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Location, CreateLocationRequest } from '../types/locations';
import { LocationType, } from '../types/locationTypes';
import { locationsApi } from '../services/locationsApi';
import { locationTypesApi } from '../services/locationTypesApi';

interface LocationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (location: Location) => void;
  location?: Location | null;
}

const LocationForm: React.FC<LocationFormProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  location = null 
}) => {
  const [formData, setFormData] = useState<CreateLocationRequest>({
    locationName: '',
    locationTypeId: undefined,
    address: '',
    city: '',
    postCode: '',
    contactPerson: '',
    contactPhone: '',
    latitude: null,        
    longitude: null,      
    active: true
  });

  const [locationTypes, setLocationTypes] = useState<LocationType[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (location) {
      setFormData({
        locationName: location.locationName,
        locationTypeId: location.locationTypeId || undefined,
        address: location.address,
        city: location.city,
        postCode: location.postCode,
        contactPerson: location.contactPerson,
        contactPhone: location.contactPhone,
        latitude: location.latitude,      
        longitude: location.longitude,    
        active: location.active
      });
    }

    fetchLocationTypes();
  }, [location]);

  const fetchLocationTypes = async () => {
    try {
      const data = await locationTypesApi.getAllLocationTypes();
      setLocationTypes(data);
    } catch (err) {
      console.error('Error fetching location types:', err);
      setError('Failed to load location types');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.locationName || !formData.address || !formData.city) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      let savedLocation: Location;
      if (location?.locationId) {
        savedLocation = await locationsApi.updateLocation(location.locationId, formData);
      } else {
        savedLocation = await locationsApi.createLocation(formData);
      }
      
      onSave(savedLocation);
      onClose();
    } catch (err) {
      setError('Failed to save location: ' + (err instanceof Error ? err.message : String(err)));
      console.error('Error saving location:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {location ? 'Edit Location' : 'Add Location'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Location Name *
              </label>
              <input
                type="text"
                value={formData.locationName}
                onChange={(e) => setFormData({...formData, locationName: e.target.value})}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Location Type
              </label>
              <select
                value={formData.locationTypeId || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    locationTypeId: value === '' ? undefined : Number(value)
                  });
                }}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
              >
                <option value="">Select a type...</option>
                {locationTypes.map(type => (
                  <option key={type.locationTypeId} value={type.locationTypeId}>
                    {type.typeName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Address *
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  City *
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Post Code *
                </label>
                <input
                  type="text"
                  value={formData.postCode}
                  onChange={(e) => setFormData({...formData, postCode: e.target.value})}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Contact Person
                </label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
            </div>

            {/* ADD THIS ENTIRE SECTION for coordinates */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Map Coordinates (Optional)
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Add coordinates to show this location on the network map. 
                <br />
                <strong>Tip:</strong> Right-click in Google Maps and select the coordinates to copy them.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    min="-90"
                    max="90"
                    value={formData.latitude ?? ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      latitude: e.target.value ? parseFloat(e.target.value) : null
                    })}
                    placeholder="e.g. 51.5074"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                  <p className="text-xs text-gray-400 mt-1">Range: -90 to 90</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    min="-180"
                    max="180"
                    value={formData.longitude ?? ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      longitude: e.target.value ? parseFloat(e.target.value) : null
                    })}
                    placeholder="e.g. -0.1278"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                  <p className="text-xs text-gray-400 mt-1">Range: -180 to 180</p>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                <strong>Example coordinates:</strong>
                <ul className="mt-1 space-y-1">
                  <li>• London, UK: 51.5074, -0.1278</li>
                  <li>• New York, US: 40.7128, -74.0060</li>
                  <li>• Los Angeles, US: 34.0522, -118.2437</li>
                </ul>
              </div>
            </div>

            {error && (
              <div className="text-red-600 bg-red-50 p-3 rounded">
                {error}
              </div>
            )}

            {error && (
              <div className="text-red-600 bg-red-50 p-3 rounded">
                {error}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationForm;